import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from './Button';
import { GRANTABLE_ROLE_OPTIONS } from '../../data/roleLevels';
import { Pill } from './Pill';
import * as api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

/**
 * MembershipManager — the one place a user's org/tenant/instance memberships are
 * viewed and mutated. A user can hold many memberships (many scopes); this
 * renders them all and lets an operator ADD another, REMOVE one, or mark one as
 * the primary/home scope. It is the single membership-mutation surface shared by
 * the Team user slideout and (later) the Multi-Tenancy edit slideouts, so the
 * add/remove/default logic can never drift between surfaces.
 *
 * Reuses the existing backend membership API verbatim
 * (getUserMemberships / addUserMembership / removeUserMembership /
 * setDefaultMembership) and the coherent scoped-child endpoints
 * (getOrganizations → getTenantsByOrg → listInstancesForTenant) so the picker
 * only ever offers a tenant that belongs to the chosen org and an instance that
 * belongs to the chosen tenant. The backend MembershipScopeValidator is the
 * backstop; these selectors are the guide.
 *
 * Props:
 *   userId        — the user whose memberships these are (required)
 *   roleOptions   — role strings the operator may grant (default: safe subset)
 *   canManageRole — optional predicate(role) → boolean to filter the role list
 *   onChanged     — optional callback fired after any successful mutation
 *   lockedOrgId / lockedTenantId — optional pre-pinned scope (used when this is
 *                   embedded under a specific org/tenant on the Multi-Tenancy page)
 */
// The grantable subset (everything but the two wildcard tiers) is shared, so a
// new built-in role reaches every member manager at once instead of being
// remembered in each of them. src/data/roleLevels.js
const DEFAULT_ROLE_OPTIONS = GRANTABLE_ROLE_OPTIONS;

function firstArray(res, ...keys) {
  const d = res && (res.data !== undefined ? res.data : res);
  if (Array.isArray(d)) return d;
  for (const k of keys) {
    if (d && Array.isArray(d[k])) return d[k];
  }
  return [];
}

export default function MembershipManager({
  userId,
  roleOptions = DEFAULT_ROLE_OPTIONS,
  canManageRole,
  onChanged,
  lockedOrgId = '',
  lockedTenantId = '',
}) {
  const { addToast } = useToast();

  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const [orgs, setOrgs] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [instances, setInstances] = useState([]);
  const [form, setForm] = useState({
    orgId: lockedOrgId || '', tenantId: lockedTenantId || '', instanceId: '', role: 'Viewer',
  });

  const availableRoles = (roleOptions || DEFAULT_ROLE_OPTIONS)
    .filter((r) => (typeof canManageRole === 'function' ? canManageRole(r) : true));

  const reload = useCallback(async () => {
    if (!userId) { setMemberships([]); return; }
    setLoading(true);
    try {
      const r = await api.getUserMemberships(userId);
      setMemberships(firstArray(r, 'memberships', 'items'));
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not load memberships', description: e?.message });
    } finally {
      setLoading(false);
    }
  }, [userId, addToast]);

  useEffect(() => { reload(); }, [reload]);

  // Orgs load once, when the add form is first opened (skipped when the scope is locked).
  useEffect(() => {
    if (!showAdd || lockedOrgId || orgs.length) return;
    api.getOrganizations().then((r) => setOrgs(firstArray(r, 'organizations', 'orgs'))).catch(() => {});
  }, [showAdd, lockedOrgId, orgs.length]);

  // Tenants scoped to the chosen org — only coherent children are offered.
  useEffect(() => {
    if (!form.orgId || lockedTenantId) { if (!lockedTenantId) setTenants([]); return; }
    api.getTenantsByOrg(form.orgId).then((r) => setTenants(firstArray(r, 'tenants'))).catch(() => setTenants([]));
  }, [form.orgId, lockedTenantId]);

  // Instances scoped to the chosen tenant (optional — blank = tenant-wide grant).
  useEffect(() => {
    if (!form.tenantId) { setInstances([]); return; }
    api.listInstancesForTenant(form.tenantId).then((r) => setInstances(firstArray(r, 'instances'))).catch(() => setInstances([]));
  }, [form.tenantId]);

  const afterChange = async () => { await reload(); if (onChanged) onChanged(); };

  const submitAdd = async () => {
    if (!form.orgId) {
      addToast({ tone: 'danger', title: 'An organization is required' });
      return;
    }
    setBusy(true);
    try {
      const res = await api.addUserMembership(userId, {
        orgId: form.orgId,
        tenantId: form.tenantId || null,
        instanceId: form.instanceId || null,
        role: form.role,
        isDefault: false,
      });
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Could not add membership', description: res.error || res.data?.error });
        return;
      }
      addToast({ tone: 'success', title: 'Membership added' });
      setForm({ orgId: lockedOrgId || '', tenantId: lockedTenantId || '', instanceId: '', role: 'Viewer' });
      setShowAdd(false);
      await afterChange();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not add membership', description: e?.message });
    } finally {
      setBusy(false);
    }
  };

  const setPrimary = async (m) => {
    const id = m.id || m.membershipId; if (!id) return;
    setBusy(true);
    try {
      const res = await api.setDefaultMembership(userId, id);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Could not set primary', description: res.error || res.data?.error });
        return;
      }
      addToast({ tone: 'success', title: 'Primary membership updated' });
      await afterChange();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not set primary', description: e?.message });
    } finally { setBusy(false); }
  };

  const remove = async (m) => {
    const id = m.id || m.membershipId; if (!id) return;
    setBusy(true);
    try {
      const res = await api.removeUserMembership(userId, id);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Could not remove membership', description: res.error || res.data?.error });
        return;
      }
      addToast({ tone: 'success', title: 'Membership removed' });
      setConfirmRemove(null);
      await afterChange();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not remove membership', description: e?.message });
    } finally { setBusy(false); }
  };

  const selectCls = 'v2-focus-ring w-full bg-v2-surface text-v2-ink border border-v2-border rounded-v2-md px-3 py-1.5 text-[12.5px] disabled:opacity-50';
  const labelCls = 'block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1';

  // Effective reach of the grant being composed, so an operator sees exactly how
  // wide a subtree grant is before adding it (org-wide covers every tenant;
  // tenant-wide covers every instance; instance is a single instance). Names come
  // from the already-loaded scoped lists; counts reflect what those lists returned.
  const orgLabel = orgs.find((o) => o.id === form.orgId)?.name || 'this organization';
  const tenantLabel = tenants.find((t) => t.id === form.tenantId)?.name || 'this tenant';
  const instanceLabel = instances.find((i) => i.id === form.instanceId)?.name || 'this instance';
  let reachHint = null;
  if (form.orgId && !form.tenantId) {
    const n = tenants.length;
    reachHint = `Grants ${form.role} across ${n ? `all ${n} tenant${n === 1 ? '' : 's'} in ` : 'every tenant in '}${orgLabel} — org-wide.`;
  } else if (form.orgId && form.tenantId && !form.instanceId) {
    const n = instances.length;
    reachHint = `Grants ${form.role} across ${n ? `all ${n} instance${n === 1 ? '' : 's'} in ` : 'every instance in '}${tenantLabel} — tenant-wide.`;
  } else if (form.orgId && form.tenantId && form.instanceId) {
    reachHint = `Grants ${form.role} on ${instanceLabel} only.`;
  }

  return (
    <div className="space-y-3">
      {/* Existing memberships */}
      {loading ? (
        <p className="text-v2-ink-muted text-[12.5px] m-0">Loading…</p>
      ) : memberships.length === 0 ? (
        <p className="text-v2-ink-muted text-[12.5px] m-0">No memberships yet. Add one below.</p>
      ) : (
        <ul className="space-y-1.5">
          {memberships.map((m) => {
            const id = m.id || m.membershipId;
            const isDefault = Boolean(m.isDefault || m.isPrimary);
            const confirming = confirmRemove === id;
            return (
              <li key={id} className="flex items-center gap-2 text-[12.5px] rounded-v2-md border border-v2-border px-2.5 py-2">
                <span className="font-semibold text-v2-ink truncate">{m.orgName || m.orgId}</span>
                <span className="text-v2-ink-muted">·</span>
                <span className="text-v2-ink truncate">{m.tenantName || m.tenantId || '—'}</span>
                {m.instanceId && (<><span className="text-v2-ink-muted">·</span><span className="text-v2-ink-muted truncate">{m.instanceName || m.instanceId}</span></>)}
                {m.role && <Pill tone="trust" size="sm">{m.role}</Pill>}
                {isDefault && <Pill tone="cyan" size="sm">Primary</Pill>}
                <span className="ml-auto flex items-center gap-1.5">
                  {confirming ? (
                    <>
                      <span className="text-[11px] text-v2-ink-muted">Remove?</span>
                      <Button size="xs" variant="danger" disabled={busy} onClick={() => remove(m)}>Yes</Button>
                      <Button size="xs" variant="ghost" disabled={busy} onClick={() => setConfirmRemove(null)}>No</Button>
                    </>
                  ) : (
                    <>
                      {!isDefault && <Button size="xs" variant="ghost" disabled={busy} onClick={() => setPrimary(m)}>Set primary</Button>}
                      <Button size="xs" variant="ghost" disabled={busy} onClick={() => setConfirmRemove(id)} iconLeft={<X size={13} />} aria-label="Remove membership" />
                    </>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/* Add membership */}
      {!showAdd ? (
        <Button size="xs" variant="secondary" iconLeft={<Plus size={14} />} onClick={() => setShowAdd(true)}>
          Add membership
        </Button>
      ) : (
        <div className="rounded-v2-md border border-v2-border bg-v2-surface-sub p-3 space-y-2.5">
          {!lockedOrgId && (
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className={labelCls}>Organization *</label>
                <select className={selectCls} value={form.orgId} disabled={busy}
                        onChange={(e) => setForm({ ...form, orgId: e.target.value, tenantId: '', instanceId: '' })}>
                  <option value="">Select org…</option>
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name || o.id}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Tenant <span className="text-v2-ink-muted normal-case">(optional — blank = org-wide)</span></label>
                <select className={selectCls} value={form.tenantId} disabled={busy || !form.orgId}
                        onChange={(e) => setForm({ ...form, tenantId: e.target.value, instanceId: '' })}>
                  <option value="">All tenants (org-wide)</option>
                  {tenants.map((t) => <option key={t.id} value={t.id}>{t.name || t.id}</option>)}
                </select>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <label className={labelCls}>Instance <span className="text-v2-ink-muted normal-case">(optional — blank = tenant-wide)</span></label>
              <select className={selectCls} value={form.instanceId} disabled={busy || !form.tenantId}
                      onChange={(e) => setForm({ ...form, instanceId: e.target.value })}>
                <option value="">All instances (tenant-wide)</option>
                {instances.map((i) => <option key={i.id} value={i.id}>{i.name || i.id}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Role *</label>
              <select className={selectCls} value={form.role} disabled={busy}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {availableRoles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          {reachHint && (
            <p className="text-[11.5px] text-v2-ink-muted m-0 leading-snug">{reachHint}</p>
          )}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="primary" disabled={busy} onClick={submitAdd}>Add membership</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setShowAdd(false); setForm({ orgId: lockedOrgId || '', tenantId: lockedTenantId || '', instanceId: '', role: 'Viewer' }); }}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
